import { action, makeObservable, observable } from "mobx";

import {
  BalanceLimitResponse,
  BalanceMonitoringParams,
  BalanceMonitoringResponse,
  BalanceRequestData,
  BalanceTransactionRequestData,
  BalanceTransactionRequestModal,
  BalanceTransactionRequestResponse,
  CashInOrCashOutBalanceRequestData,
  GetBalanceHistoryRequest,
  GetBalanceMonitoringRequest,
  LimitMonitoringRequestParams,
  LimitMonitoringResponse,
  LimitOrBalanceUpdateData,
  Request,
  RequestResponse,
  WalletResponse,
} from "../models";
import { downloadFileFromHttpResponse } from "../helpers/file-downloader";
import { HttpResponse, walletController } from "../api";
import { DEFAULT_RESPONSE_LIST } from "../constants";
import { TransactionTableTabs } from "../enums";
import { LocalStorageHelper } from "../helpers";
import { RootStore } from "./index";

export class WalletStore {
  private rootStore: RootStore;

  public wallet = new HttpResponse<Partial<WalletResponse>>({});
  public balanceLimitData = new HttpResponse<BalanceLimitResponse>(DEFAULT_RESPONSE_LIST);
  public balanceMonitoringList = new HttpResponse<BalanceMonitoringResponse>(DEFAULT_RESPONSE_LIST);
  public limitMonitoringList = new HttpResponse<LimitMonitoringResponse>(DEFAULT_RESPONSE_LIST);
  public requestList = new HttpResponse<RequestResponse>(DEFAULT_RESPONSE_LIST);
  public transactionActiveTab = LocalStorageHelper.getItem("transactionActiveTab") || TransactionTableTabs.BalanceMonitoring;

  public balanceRequestModal: BalanceTransactionRequestModal = {
    isOpen: false,
    data: {} as Request,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      wallet: observable,
      setWallet: action.bound,
      balanceLimitData: observable,
      balanceMonitoringList: observable,
      requestList: observable,
      balanceRequestModal: observable,
      limitMonitoringList: observable,
      transactionActiveTab: observable,
      setBalanceLimitHistory: action.bound,
      setBalanceMonitoringList: action.bound,
      setRequestList: action.bound,
      setBalanceRequestModal: action.bound,
      setLimitMonitoringList: action.bound,
      setTransactionActiveTab: action.bound,
    });
  }

  public async balanceTransaction(data: BalanceTransactionRequestData): Promise<BalanceTransactionRequestResponse> {
    return await walletController.balanceTransaction(data);
  }

  public async balanceRequestApproveOrReject(data: BalanceRequestData): Promise<any> {
    return await walletController.balanceRequestApproveOrReject(data);
  }

  public async cashOutBalance(data: CashInOrCashOutBalanceRequestData): Promise<void> {
    return await walletController.cashOutBalance(data);
  }

  public async cashInBalance(data: CashInOrCashOutBalanceRequestData): Promise<void> {
    return await walletController.cashInBalance(data);
  }

  public setWallet(value: HttpResponse<Partial<WalletResponse>>): void {
    this.wallet = value;
  }

  public setBalanceLimitHistory(value: HttpResponse<BalanceLimitResponse>): void {
    this.balanceLimitData = value;
  }

  public setBalanceMonitoringList(value: HttpResponse<BalanceMonitoringResponse>): void {
    this.balanceMonitoringList = value;
  }

  public setLimitMonitoringList(value: HttpResponse<LimitMonitoringResponse>): void {
    this.limitMonitoringList = value;
  }

  public setRequestList(value: HttpResponse<RequestResponse>): void {
    this.requestList = value;
  }

  public setBalanceRequestModal(value: BalanceTransactionRequestModal): void {
    this.balanceRequestModal = value;
  }

  public setTransactionActiveTab(value: TransactionTableTabs): void {
    this.transactionActiveTab = value;
  }

  public async getWallet(userId: number): Promise<void> {
    this.setWallet(this.wallet.fetching());
    const data = await walletController.getWallet(userId);
    this.setWallet(this.wallet.fetched(data));
  }

  public async getBalanceHistory(params: GetBalanceHistoryRequest,): Promise<void> {
    this.setBalanceLimitHistory(this.balanceLimitData.fetching());
    const data = await walletController.getBalanceHistory(params);
    this.setBalanceLimitHistory(this.balanceLimitData.fetched(data));
  }

  public async getRequestList(params: GetBalanceMonitoringRequest): Promise<void> {
    this.setRequestList(this.requestList.fetching());
    const data = await walletController.getRequests(params);
    this.setRequestList(this.requestList.fetched(data));
  }

  public async getLimitHistory(params: GetBalanceHistoryRequest): Promise<void> {
    this.setBalanceLimitHistory(this.balanceLimitData.fetching());
    const data = await walletController.getLimitHistory(params);
    this.setBalanceLimitHistory(this.balanceLimitData.fetched(data));
  }

  public async getBalanceMonitoringList(params: BalanceMonitoringParams): Promise<void> {
    this.setBalanceMonitoringList(this.balanceMonitoringList.fetching());
    const data = await walletController.getBalanceMonitoringTransactions(params);
    this.setBalanceMonitoringList(this.balanceMonitoringList.fetched(data));
  }

  public async getLimitMonitoringList(params: LimitMonitoringRequestParams): Promise<void> {
    this.setLimitMonitoringList(this.limitMonitoringList.fetching());
    const data = await walletController.getLimitMonitoringTransactions(params);
    this.setLimitMonitoringList(this.limitMonitoringList.fetched(data));
  }

  public async updateLimit(id: number, data: LimitOrBalanceUpdateData, isWithDraw: boolean) {
    return isWithDraw ? walletController.removeLimit(id, data) : walletController.updateLimit(id, data);
  }

  public async updateBalance(id: number, data: LimitOrBalanceUpdateData, isWithDraw: boolean) {
    return isWithDraw ? walletController.removeBalance(id, data) : walletController.updateBalance(id, data);
  }

  public async resetBalanceMonitoringList() {
    this.setBalanceMonitoringList(this.balanceMonitoringList.fetched(DEFAULT_RESPONSE_LIST));
  }

  public async resetLimitMonitoringList() {
    this.setLimitMonitoringList(this.limitMonitoringList.fetched(DEFAULT_RESPONSE_LIST));
  }

  public async exportBalanceHistoryExcel(params: BalanceMonitoringParams) {
    const res = await walletController.exportBalanceHistoryExcel(params);
    if (!res.ok) {
      throw new Error('Something went wrong while exporting.');
    }
    try {
      downloadFileFromHttpResponse(res, 'balanceHistory.xlsx');
    } catch (error) {
      console.error(error);
    }
    return res;
  }

  public async exportBalanceHistoryCsv(params: BalanceMonitoringParams) {
    const res = await walletController.exportBalanceHistoryCsv(params);
    if (!res.ok) {
      throw new Error('Something went wrong while exporting.');
    }
    try {
      downloadFileFromHttpResponse(res, 'balanceHistory.csv');
    } catch (error) {
      console.error(error);
    }
    return res;
  }

  public async exportLimitHistoryExcel(params: LimitMonitoringRequestParams) {
    const res = await walletController.exportLimitHistoryExcel(params);
    if (!res.ok) {
      throw new Error('Something went wrong while exporting.');
    }
    try {
      downloadFileFromHttpResponse(res, 'limitHistory.xlsx');
    } catch (error) {
      console.error(error);
    }
    return res;
  }

  public async exportLimitHistoryCsv(params: LimitMonitoringRequestParams) {
    const res = await walletController.exportLimitHistoryCsv(params);
    if (!res.ok) {
      throw new Error('Something went wrong while exporting.');
    }
    try {
      downloadFileFromHttpResponse(res, 'limitHistory.csv');
    } catch (error) {
      console.error(error);
    }
    return res;
  }

}
